/* eslint-disable no-nested-ternary */
import { Mic } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import useConnectionTest from 'hooks/useConnectionTest';
import NetworkTest from 'opentok-network-test-js';
import { createSession, OTPublisher, SessionHelper } from 'opentok-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactHighcharts from 'react-highcharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { convertFailedTestsToString, getChartInitialConfig, graphIntermediateStats, rateMosScore } from 'utils/chart';
import DialogClose from 'views/components/buttons/DialogClose';
import PopularCard from 'views/components/skeleton/PopularCard';

const MENU_OPTIONS = [30, 25, 20, 15, 10, 5];

type Props = {
  onClose?: () => void;
};

const ConnectionTest: React.FC<Props> = (props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { onClose = () => navigate('/') } = props;
  const { testSession } = useConnectionTest();

  const [showConnectivity, setShowConnectivity] = useState(false);
  const [showQuality, setShowQuality] = useState(false);
  const [showAudioVideoTest, setShowAudioVideoTest] = useState(false);

  const [duration, setDuration] = useState(MENU_OPTIONS[0]);
  const [audioOnly, setAudioOnly] = useState(false);
  const otNetworkRef = useRef<any>(null);

  const handleStartTest = useCallback(() => {
    if (showConnectivity) {
      setShowConnectivity(false);
      setShowQuality(false);
      setShowAudioVideoTest(false);
      return;
    }

    if (testSession) {
      const config = {
        apiKey: import.meta.env.VITE_TOKBOX_API_KEY ?? '',
        sessionId: testSession.sessionId,
        token: testSession.token
      };

      const options = {
        audioOnly,
        timeout: duration * 1000
      };

      const otNetworkTest = new NetworkTest(OT as any, config, options);
      otNetworkRef.current = otNetworkTest;
      setShowConnectivity(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSession, duration, audioOnly, showConnectivity]);

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
      <DialogTitle>
        <FormattedMessage defaultMessage="Network Test" />
        <DialogClose onClose={onClose} />
      </DialogTitle>

      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {!testSession && <PopularCard />}

        {!showConnectivity && testSession && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControlLabel
                sx={{ height: '100%' }}
                control={<Checkbox checked={audioOnly} onChange={(e: any) => setAudioOnly(e.target.checked)} />}
                label={intl.formatMessage({ defaultMessage: 'Audio Only' })}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage defaultMessage="Maximum Duration" />
                </InputLabel>
                <Select
                  label={intl.formatMessage({ defaultMessage: 'Maximum Duration' })}
                  value={duration}
                  onChange={(e: any) => setDuration(e.target.value)}
                >
                  {MENU_OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {showConnectivity && <TestConnectivity onStreamEnd={() => setShowQuality(true)} otNetworkTest={otNetworkRef.current} />}
        {showAudioVideoTest && <AudioVideoTest testSession={testSession} audioOnly={audioOnly} />}
        {showQuality && (
          <TestQuality otNetworkTest={otNetworkRef.current} audioOnly={audioOnly} onResults={() => setShowAudioVideoTest(true)} />
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button type="button" variant="outlined" onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleStartTest} variant="contained" disabled={false}>
            {showConnectivity ? <FormattedMessage defaultMessage="Reset Test" /> : <FormattedMessage defaultMessage="Start Test" />}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const TestConnectivity: React.FC<any> = ({ otNetworkTest, onStreamEnd }) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setMessage(intl.formatMessage({ defaultMessage: 'Test in progress' }));

    otNetworkTest
      .testConnectivity()
      .then((results) => {
        if (results.success) {
          setMessage(intl.formatMessage({ defaultMessage: 'Passed' }));
          setPassed(true);
          setLoading(false);
          onStreamEnd();
        } else {
          const errorMsg = `Failed tests: ${convertFailedTestsToString(results.failedTests)}`;
          setMessage(errorMsg);
          setPassed(false);
          setLoading(false);
          onStreamEnd();
        }
      })
      .catch((error) => {
        setMessage('Connectivity Error');
        setPassed(false);
        setLoading(false);
        onStreamEnd();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box bgcolor="grey.200" display="flex" flexDirection="column" alignItems="center" p={2} sx={{ mb: 2 }}>
      <Typography sx={{ mb: 1 }} variant="h4">
        <FormattedMessage defaultMessage="Testing Connectivity" />
      </Typography>
      <Typography sx={{ mb: 1 }}>{message}</Typography>

      {loading ? <LinearProgress sx={{ width: 50 }} /> : passed ? <CheckCircleIcon /> : <CancelIcon />}
    </Box>
  );
};

const TestQuality: React.FC<any> = ({ otNetworkTest, audioOnly, onResults }) => {
  const intl = useIntl();
  const [message, setMessage] = useState('');
  const [audioConfig] = useState(getChartInitialConfig('audio'));
  const [videoConfig] = useState(getChartInitialConfig('video'));
  const [results, setResults] = useState<any>(null);

  const audioRef = useRef<any>(null);
  const videoRef = useRef<any>(null);

  useEffect(() => {
    const resultCount = { audio: 0, video: 0 };
    const prevBitsReceived = { audio: 0, video: 0 };
    setMessage(intl.formatMessage({ defaultMessage: 'Test in progress' }));

    otNetworkTest
      .testQuality((stats) => {
        const audioChart = audioRef?.current?.getChart();
        const videoChart = videoRef?.current?.getChart();

        if (audioChart) {
          graphIntermediateStats(audioChart, prevBitsReceived, resultCount, 'audio', stats);
        }

        if (videoChart) {
          graphIntermediateStats(videoChart, prevBitsReceived, resultCount, 'video', stats);
        }
      })
      .then((results) => {
        const audioMos = results.audio.mos;
        const videoMos = results.video.mos;

        let statusMsg = '';
        if ((results.audio.supported && results.video.supported) || (audioOnly && results.audio.supported)) {
          statusMsg = intl.formatMessage({ defaultMessage: 'Passed' });
        } else if (!results.audio.supported) {
          statusMsg = intl.formatMessage({ defaultMessage: 'Check your audio setup' });
        } else {
          statusMsg = intl.formatMessage({ defaultMessage: 'Check your video setup' });
        }
        setMessage(statusMsg);

        const setting = {
          passed: (results.audio.supported && results.video.supported) || (audioOnly && results.audio.supported),
          audioSupported: results.audio.supported ? 'Yes' : 'No',
          audioMos: `${audioMos.toFixed(2)} (${rateMosScore(audioMos)})`,
          audioBitrate: results.audio.bitrate ? `${(results.audio.bitrate / 1000).toFixed(2)} kbps` : '--',
          audioPlr: results.audio.supported ? `${(results.audio.packetLossRatio / 100).toFixed(2)}%` : '--',
          videoSupported: results.video.supported ? 'Yes' : 'No',
          videoMos: `${videoMos.toFixed(2)} (${rateMosScore(videoMos)})`,
          videoBitrate: results.video.bitrate ? `${(results.video.bitrate / 1000).toFixed(2)} kbps` : '--',
          videoPlr: results.video.supported ? `${(results.video.packetLossRatio / 100).toFixed(2)}%` : '--',
          videoRecommendedResolution: results.video.recommendedResolution || '--',
          videoRecommendedFrameRate: results.video.recommendedFrameRate ? `${results.video.recommendedFrameRate} fps` : '--'
        };
        setResults(setting);
        onResults();
      })
      .catch(() => {
        setMessage(intl.formatMessage({ defaultMessage: 'Failed' }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioRef, videoRef, audioOnly, otNetworkTest]);

  return (
    <Box bgcolor="grey.200" p={2}>
      <Grid spacing={2} container>
        <Grid item container xs={12} display="flex" flexDirection="column" alignItems="center">
          <Typography sx={{ mb: 1 }} variant="h4">
            <FormattedMessage defaultMessage="Testing Quality" />
          </Typography>
          <Typography sx={{ mb: 1 }}>{message}</Typography>
          {!results ? <LinearProgress sx={{ width: 50 }} /> : results.passed ? <CheckCircleIcon /> : <CancelIcon />}
        </Grid>

        {audioOnly && <Grid item xs={3} />}

        <Grid item xs={6}>
          {results && (
            <Box sx={{ mb: 2 }} display="flex" flexDirection="column" alignItems="center">
              <Typography>
                <FormattedMessage defaultMessage="Supported:" />
                <span>{results.audioSupported}</span>
              </Typography>
              <Typography>
                <FormattedMessage defaultMessage="Quality:" />
                <span>{results?.audioMos}</span>
              </Typography>
              <Typography>
                <FormattedMessage defaultMessage="Bitrate:" />
                <span>{results?.audioBitrate}</span>
              </Typography>
              <Typography>
                <FormattedMessage defaultMessage="Packet loss ratio:" />
                <span>{results?.videoPlr}</span>
              </Typography>
              <Typography sx={{ opacity: 0 }}>
                <FormattedMessage defaultMessage="Recommended resolution:" />
              </Typography>
              <Typography sx={{ opacity: 0 }}>
                <FormattedMessage defaultMessage="Recommended frame rate:" />
              </Typography>
            </Box>
          )}
          <ReactHighcharts style={{ height: 200 }} config={audioConfig} ref={audioRef} />
        </Grid>

        {!audioOnly && (
          <Grid item xs={6}>
            {results && (
              <Box sx={{ mb: 2 }} display="flex" flexDirection="column" alignItems="center">
                <Typography>
                  <FormattedMessage defaultMessage="Supported:" />
                  <span>{results.videoSupported}</span>
                </Typography>
                <Typography>
                  <FormattedMessage defaultMessage="Quality:" />
                  <span>{results?.videoMos}</span>
                </Typography>
                <Typography>
                  <FormattedMessage defaultMessage="Bitrate:" />
                  <span>{results?.videoBitrate}</span>
                </Typography>
                <Typography>
                  <FormattedMessage defaultMessage="Packet loss ratio:" />
                  <span>{results?.audioPlr}</span>
                </Typography>
                <Typography>
                  <FormattedMessage defaultMessage="Recommended resolution:" />
                  <span>{results?.videoRecommendedResolution}</span>
                </Typography>
                <Typography>
                  <FormattedMessage defaultMessage="Recommended frame rate:" />
                  <span>{results?.videoRecommendedFrameRate}</span>
                </Typography>
              </Box>
            )}
            <ReactHighcharts style={{ height: 200 }} config={videoConfig} ref={videoRef} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

interface AudioVideoTestProps {
  testSession: any;
  audioOnly: boolean;
}

function AudioVideoTest({ testSession, audioOnly }: AudioVideoTestProps) {
  const [videoInput, setVideoInput] = useState('');
  const [audioInput, setAudioInput] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [sessionHelper, setSessionHelper] = useState<SessionHelper>();

  useEffect(() => {
    const config = {
      apiKey: import.meta.env.VITE_TOKBOX_API_KEY ?? '',
      sessionId: testSession.sessionId,
      token: testSession.token
    };
    setSessionHelper(createSession(config));
  }, [testSession]);

  if (!sessionHelper) {
    return null;
  }

  return (
    <Box bgcolor="grey.200" display="flex" flexDirection="column" alignItems="center" p={2} sx={{ mb: 2 }}>
      <Typography sx={{ mb: 1 }} variant="h4">
        <FormattedMessage defaultMessage="Audio/Video Test" />
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={audioOnly ? 3 : 6} sx={{ visibility: audioOnly ? 'hidden' : 'initial' }}>
          <OTPublisher
            properties={{
              width: '100%',
              height: 248,
              fitMode: 'contain',
              showControls: false
            }}
            session={sessionHelper.session}
            eventHandlers={{
              audioLevelUpdated(event) {
                setAudioLevel(event.audioLevel);
              }
            }}
            onPublish={() => {
              (OT as any).getUserMedia().then((media) => {
                setVideoInput(media.getVideoTracks()[0].label);
                setAudioInput(media.getAudioTracks()[0].label);
              });
            }}
          />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>{videoInput}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Stack sx={{ height: 248, justifyContent: 'center' }}>
            <Stack direction="row" spacing={1}>
              <Mic />
              <meter value={audioLevel} style={{ flex: 1, height: 20, borderRadius: 3 }} />
            </Stack>
            <Typography sx={{ mt: 2, textAlign: 'center', fontSize: 13 }}>
              <FormattedMessage defaultMessage="Speak to test your microphone and volume" />
            </Typography>
          </Stack>
          <Typography sx={{ mt: 2, textAlign: 'center' }}>{audioInput}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ConnectionTest;
